// Back-office de l'exploitant : superviser le produit lui-même.
//
// Quatre onglets en LECTURE : les comptes, le volume de données par
// organisation, la consommation d'IA et l'état de la plateforme. On n'y voit
// que des compteurs — jamais un verbatim, un insight ni un enregistrement : ces
// données sont celles des organisations clientes.
//
// Un cinquième, « Clés », est le seul qui ÉCRIT : les clés des services que
// Zygosight appelle. Une clé saisie n'est jamais réaffichée.
//
// L'écran ne décide pas de qui entre : le serveur répond 404 à tout compte qui
// n'est pas super-administrateur, et l'écran affiche alors « introuvable ».
import { useState, type FormEvent, type ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { ApiRequestError } from '../api/client';
import { Button, Chip, chipState, ErrorBanner, Eyebrow, Field, Select, Spinner, Stat } from '../components/ui';
import { Link } from '../router';
import { APP_VERSION } from '../version';
import { useMe } from '../api/queries';
import {
  DATA_COLUMNS,
  useBoAccountAction,
  useBoAccounts,
  useBoCreateAccount,
  type BoAccountAction,
  type BoOrganization,
  type BoOrgStatus,
  useBoAi,
  useBoData,
  useBoKeys,
  useBoMaintenance,
  useBoPlatform,
  useBoSaveMaintenance,
  type BoMaintenanceView,
  useClearBoKey,
  useSetBoKey,
  useTestBoKey,
  type BoSetting,
  type BoUsageLine,
} from './api';
import './backoffice.css';
import { FeedbackTab } from './FeedbackTab';

const TABS = [
  ['comptes', 'Comptes'],
  ['donnees', 'Données'],
  ['ia', 'IA'],
  ['plateforme', 'Plateforme'],
  ['cles', 'Clés'],
  ['maintenance', 'Maintenance'],
  ['retours', 'Retours'],
] as const;
type Tab = (typeof TABS)[number][0];

const TASK_LABELS: Record<string, string> = {
  extract: 'Extraction de verbatims et protocoles',
  transcribe: 'Transcription d’entretiens',
  copilot: 'Copilote, synthèses et propositions',
  pillars: 'Déduction des piliers',
  context: 'Lecture du site du produit',
  'embed-document': 'Indexation sémantique',
  'embed-query': 'Recherche sémantique',
};

const int = new Intl.NumberFormat('fr-FR');
const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtDateTime = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—';

function fmtBytes(bytes: number): string {
  if (bytes <= 0) return '—';
  const units = ['o', 'Ko', 'Mo', 'Go', 'To'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** i;
  return `${value.toLocaleString('fr-FR', { maximumFractionDigits: value < 10 && i > 0 ? 1 : 0 })} ${units[i]}`;
}

// Les petits montants comptent : une journée d'IA coûte souvent quelques
// centimes, et « 0,00 $ » ferait croire qu'elle est gratuite.
function fmtCost(usd: number): string {
  const digits = usd > 0 && usd < 1 ? 4 : 2;
  return `${usd.toLocaleString('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits })} $`;
}

function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} M`;
  if (n >= 1_000) return `${(n / 1_000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} k`;
  return int.format(n);
}

function fmtUptime(seconds: number): string {
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))} min`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86_400)} j`;
}

/** Attente, erreur, puis contenu : le même squelette pour les quatre onglets. */
function Loaded<T>({ query, children }: { query: UseQueryResult<T>; children: (data: T) => ReactNode }) {
  if (query.isPending) return <Spinner label="Chargement…" />;
  if (query.isError || !query.data) {
    const message = query.error instanceof ApiRequestError ? query.error.message : 'Lecture impossible.';
    return <ErrorBanner>{message}</ErrorBanner>;
  }
  return <>{children(query.data)}</>;
}

// --- Comptes -----------------------------------------------------------------

const ORG_STATUS: Record<BoOrgStatus, { label: string; tone: string }> = {
  pending: { label: 'En attente', tone: 'tone-warn' },
  active: { label: 'Ouvert', tone: 'tone-good' },
  suspended: { label: 'Suspendu', tone: 'tone-bad' },
};

function boError(cause: unknown): string {
  return cause instanceof ApiRequestError ? cause.message : 'Une erreur inattendue est survenue.';
}

/** Approuver / refuser une demande, suspendre ou rétablir un compte ouvert. */
function AccountActions({ org }: { org: BoOrganization }) {
  const act = useBoAccountAction();
  // Refuser SUPPRIME la demande : on le fait confirmer.
  const [confirming, setConfirming] = useState(false);
  const run = (action: BoAccountAction) => act.mutate({ id: org.id, action });
  return (
    <div className="bo-actions">
      {org.status === 'pending' ? (
        confirming ? (
          <>
            <span className="bo-meta">Supprimer cette demande ?</span>
            <Button variant="ghost" onClick={() => setConfirming(false)}>Annuler</Button>
            <Button variant="danger" loading={act.isPending} onClick={() => run('reject')}>Oui, refuser</Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={() => setConfirming(true)}>Refuser</Button>
            <Button variant="primary" loading={act.isPending} onClick={() => run('approve')}>Approuver</Button>
          </>
        )
      ) : org.status === 'active' ? (
        <Button variant="ghost" loading={act.isPending} onClick={() => run('suspend')}>Suspendre</Button>
      ) : (
        <Button variant="ghost" loading={act.isPending} onClick={() => run('approve')}>Rétablir l’accès</Button>
      )}
      {act.error ? <ErrorBanner>{boError(act.error)}</ErrorBanner> : null}
    </div>
  );
}

/** Créer un compte client : déjà approuvé, avec un mot de passe provisoire. */
function CreateAccountForm() {
  const create = useBoCreateAccount();
  const [organizationName, setOrganizationName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const ready = Boolean(organizationName.trim() && name.trim() && email.trim());
  const created = create.data?.created;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ready) return;
    try {
      await create.mutateAsync({ organizationName, name, email });
      setOrganizationName('');
      setName('');
      setEmail('');
    } catch {
      /* rendu par le bandeau */
    }
  }

  return (
    <section className="zy-card bo-section">
      <div className="bo-section-head">
        <h2 className="t-h3">Créer un compte client</h2>
        <span className="bo-meta">Déjà approuvé — la personne devient admin de son organisation</span>
      </div>
      <form className="bo-create" onSubmit={submit} noValidate>
        <Field label="Organisation" value={organizationName} onChange={(e) => setOrganizationName(e.target.value)} />
        <Field label="Nom de la personne" value={name} onChange={(e) => setName(e.target.value)} />
        <Field label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Button type="submit" variant="accent" loading={create.isPending} disabled={!ready}>Créer le compte</Button>
      </form>
      {create.error ? <ErrorBanner>{boError(create.error)}</ErrorBanner> : null}
      {created ? (
        <p className="bo-created" role="status">
          Compte créé pour <strong>{created.email}</strong>. Mot de passe provisoire :{' '}
          <code>{created.temporaryPassword}</code>
          <br />
          Notez-le maintenant et transmettez-le vous-même : il ne sera plus jamais affiché.
        </p>
      ) : null}
    </section>
  );
}

function OrganizationSection({ org }: { org: BoOrganization }) {
  const st = ORG_STATUS[org.status];
  return (
    <section className={`zy-card bo-section${org.status === 'pending' ? ' bo-section-pending' : ''}`}>
      <div className="bo-section-head">
        <h2 className="t-h3">
          {org.name} <Chip state={chipState(st.tone)}>{st.label}</Chip>
        </h2>
        <span className="bo-meta">
          {org.status === 'pending' ? 'Demandée' : 'Créée'} le {fmtDate(org.createdAt)} · {org.users.length} compte{org.users.length > 1 ? 's' : ''}
        </span>
        <AccountActions org={org} />
      </div>
      <div className="zy-table-cadre">
        <table className="zy-table zy-table--dense">
          <thead>
            <tr>
              <th>Nom</th>
              <th>E-mail</th>
              <th>Rôle</th>
              <th>État</th>
              <th>Créé le</th>
              <th>Conditions acceptées</th>
              <th>Session en cours</th>
            </tr>
          </thead>
          <tbody>
            {org.users.map((u) => (
              <tr key={u.id}>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td>{u.orgRole === 'admin' ? 'Admin' : 'Membre'}</td>
                <td>{u.status === 'active' ? 'Actif' : u.status === 'invited' ? 'Invité' : 'Désactivé'}</td>
                <td>{fmtDate(u.createdAt)}</td>
                <td>{u.termsAcceptedAt ? fmtDate(u.termsAcceptedAt) : 'Non'}</td>
                <td>{u.lastSeen ? fmtDateTime(u.lastSeen) : 'Aucune'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AccountsTab() {
  const accounts = useBoAccounts();
  return (
    <Loaded query={accounts}>
      {(a) => {
        // Les demandes d'abord : c'est ce qui attend une décision.
        const pending = a.organizations.filter((o) => o.status === 'pending');
        const others = a.organizations.filter((o) => o.status !== 'pending');
        return (
          <>
            <div className="bo-stats">
              <Stat label="À approuver" value={int.format(a.totals.pending)} />
              <Stat label="Organisations" value={int.format(a.totals.organizations)} />
              <Stat label="Comptes" value={int.format(a.totals.users)} />
              <Stat label="Nouveaux · 7 jours" value={int.format(a.totals.newUsers7d)} />
              <Stat label="Nouveaux · 30 jours" value={int.format(a.totals.newUsers30d)} />
            </div>
            {pending.length > 0 ? (
              <p className="bo-note bo-note-pending" role="status">
                {pending.length} demande{pending.length > 1 ? 's' : ''} de compte en attente : tant que vous ne l’avez pas
                approuvée, personne ne peut s’y connecter.
              </p>
            ) : null}
            {pending.map((o) => (
              <OrganizationSection key={o.id} org={o} />
            ))}
            <CreateAccountForm />
            {others.map((o) => (
              <OrganizationSection key={o.id} org={o} />
            ))}
            <p className="bo-note">
              « Session en cours » donne la dernière activité d’une session encore ouverte. Une déconnexion l’efface :
              « Aucune » ne veut pas dire que la personne ne s’est jamais connectée. Suspendre coupe l’accès tout de
              suite et garde les données.
            </p>
          </>
        );
      }}
    </Loaded>
  );
}

// --- Données -----------------------------------------------------------------

function DataTab() {
  const data = useBoData();
  return (
    <Loaded query={data}>
      {(d) => (
        <>
          <div className="bo-stats">
            <Stat label="Projets" value={int.format(d.totals.projects)} />
            <Stat label="Études" value={int.format(d.totals.studies)} />
            <Stat label="Verbatims" value={int.format(d.totals.verbatims)} />
            <Stat
              label="Enregistrements"
              value={fmtBytes(d.totals.recordingBytes)}
              hint={`${int.format(d.totals.recordings)} fichier${d.totals.recordings > 1 ? 's' : ''}`}
            />
          </div>
          <section className="zy-card bo-section">
            <div className="bo-section-head">
              <h2 className="t-h3">Volume par organisation</h2>
              <span className="bo-meta">Des compteurs uniquement : le contenu de recherche n’est pas lu.</span>
            </div>
            <div className="zy-table-cadre">
              <table className="zy-table zy-table--dense">
                <thead>
                  <tr>
                    <th>Organisation</th>
                    {DATA_COLUMNS.map(([key, label]) => (
                      <th className="num" key={key}>{label}</th>
                    ))}
                    <th className="num">Enregistrements</th>
                    <th>Dernière activité</th>
                  </tr>
                </thead>
                <tbody>
                  {d.organizations.map((o) => (
                    <tr key={o.id}>
                      <td>{o.name}</td>
                      {DATA_COLUMNS.map(([key]) => (
                        <td className="num" key={key}>{int.format(o[key])}</td>
                      ))}
                      <td className="num">{fmtBytes(o.recordingBytes)}</td>
                      <td>{fmtDateTime(o.lastSeen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </Loaded>
  );
}

// --- IA ----------------------------------------------------------------------

function UsageTable({ title, first, lines }: { title: string; first: string; lines: BoUsageLine[] }) {
  return (
    <section className="zy-card bo-section">
      <div className="bo-section-head">
        <h2 className="t-h3">{title}</h2>
      </div>
      <div className="zy-table-cadre">
        <table className="zy-table zy-table--dense">
          <thead>
            <tr>
              <th>{first}</th>
              <th className="num">Appels</th>
              <th className="num">Tokens entrée</th>
              <th className="num">Tokens sortie</th>
              <th className="num">Coût estimé</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.key}>
                <td>{l.label}</td>
                <td className="num">{int.format(l.calls)}</td>
                <td className="num">{fmtTokens(l.inputTokens)}</td>
                <td className="num">{fmtTokens(l.outputTokens)}</td>
                <td className="num">{fmtCost(l.cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const PERIODS = [7, 30, 90] as const;

function AiTab() {
  const [days, setDays] = useState<number>(30);
  const ai = useBoAi(days);
  return (
    <>
      <div className="bo-periods" role="group" aria-label="Période">
        {PERIODS.map((p) => (
          <Button key={p} variant={p === days ? 'primary' : 'ghost'} onClick={() => setDays(p)}>
            {p} jours
          </Button>
        ))}
      </div>
      <Loaded query={ai}>
        {(a) => (
          <p className="bo-note">
            {a.ownKey.configured === 0
              ? 'Aucune organisation n’utilise sa propre clé d’IA.'
              : `${a.ownKey.configured} organisation${a.ownKey.configured > 1 ? 's utilisent leur' : ' utilise sa'} propre clé d’IA : ${int.format(a.ownKey.calls)} appel${a.ownKey.calls > 1 ? 's' : ''} sur la période, à leurs frais et hors des chiffres ci-dessous.`}
          </p>
        )}
      </Loaded>
      <Loaded query={ai}>
        {(a) =>
          a.total.calls === 0 ? (
            <section className="zy-card bo-section">
              <p>Aucun appel à l’IA enregistré sur cette période.</p>
              <p className="bo-note">
                {a.since
                  ? `La mesure a commencé le ${fmtDate(a.since)}.`
                  : 'La mesure démarre avec cette version : les appels antérieurs n’ont pas été comptés.'}
              </p>
            </section>
          ) : (
            <>
              <div className="bo-stats">
                <Stat label={`Coût estimé · ${a.days} jours`} value={fmtCost(a.total.cost)} />
                <Stat label="Appels" value={int.format(a.total.calls)} />
                <Stat label="Tokens en entrée" value={fmtTokens(a.total.inputTokens)} />
                <Stat label="Tokens en sortie" value={fmtTokens(a.total.outputTokens)} />
              </div>
              {a.unpricedModels.length > 0 ? (
                <ErrorBanner>
                  Pas de prix connu pour {a.unpricedModels.join(', ')} : ses appels sont comptés mais pas chiffrés. Le
                  coût affiché est donc sous-estimé.
                </ErrorBanner>
              ) : null}
              <UsageTable title="Par organisation" first="Organisation" lines={a.byOrganization} />
              <UsageTable
                title="Par usage"
                first="Usage"
                lines={a.byTask.map((t) => ({ ...t, label: TASK_LABELS[t.key] ?? t.label }))}
              />
              <UsageTable title="Par modèle" first="Modèle" lines={a.byModel} />
              <UsageTable title="Par mois (6 derniers)" first="Mois" lines={a.byMonth} />
              <p className="bo-note">
                Estimation : quantités mesurées × prix catalogue, en dollars. La facture du fournisseur fait foi.
                {a.since ? ` Mesure commencée le ${fmtDate(a.since)}.` : ''}
              </p>
            </>
          )
        }
      </Loaded>
    </>
  );
}

// --- Plateforme --------------------------------------------------------------

function Facts({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="bo-facts">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function PlatformTab() {
  const platform = useBoPlatform();
  return (
    <Loaded query={platform}>
      {(p) => (
        <>
          {p.warnings.length > 0 ? (
            <section className="zy-card bo-section bo-warnings">
              <h2 className="t-h3">À corriger</h2>
              <ul>
                {p.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </section>
          ) : null}
          <div className="bo-grid">
            <section className="zy-card bo-section">
              <h2 className="t-h3">Serveur</h2>
              <Facts
                rows={[
                  ['Environnement', p.runtime.environment],
                  // Deux versions : si elles diffèrent, le navigateur garde une
                  // ancienne interface en cache (ou le déploiement est partiel).
                  ['Version du serveur', p.runtime.version],
                  [
                    'Version de l’interface',
                    p.runtime.version === APP_VERSION ? APP_VERSION : `${APP_VERSION} — différente du serveur, rechargez la page`,
                  ],
                  ['Service Cloud Run', p.runtime.service ?? '—'],
                  ['Révision', p.runtime.revision ?? '—'],
                  ['Projet Google Cloud', p.runtime.gcpProject ?? '—'],
                  ['Node', p.runtime.node],
                  ['Démarré depuis', fmtUptime(p.runtime.uptimeSeconds)],
                ]}
              />
            </section>
            <section className="zy-card bo-section">
              <h2 className="t-h3">Base de données</h2>
              <Facts
                rows={[
                  ['Moteur', p.database.provider ? `${p.database.engine} · ${p.database.provider}` : p.database.engine],
                  ['Hôte', p.database.host ?? '—'],
                  ['Rôle de connexion', p.database.role ?? '—'],
                  [
                    'Cloisonnement par la base (RLS)',
                    p.database.bypassesRls === null ? '—' : p.database.bypassesRls ? 'Contourné par ce rôle' : 'Appliqué',
                  ],
                  ['Migrations', `${p.migrations.applied} sur ${p.migrations.available}`],
                  ['Dernière migration', p.migrations.last ?? '—'],
                ]}
              />
            </section>
            <section className="zy-card bo-section">
              <h2 className="t-h3">IA</h2>
              <Facts
                rows={[
                  ['Fournisseur', p.ai.configured ? p.ai.provider : `${p.ai.provider} (non configuré)`],
                  ['Région', p.ai.location ?? '—'],
                  ['Extraction', p.ai.models['extract'] ?? '—'],
                  ['Copilote', p.ai.models['copilot'] ?? '—'],
                  ['Transcription', p.ai.transcription ? (p.ai.models['transcribe'] ?? '—') : 'Indisponible'],
                  ['Recherche sémantique', p.ai.embeddings ?? 'Désactivée (lexical seul)'],
                ]}
              />
            </section>
            <section className="zy-card bo-section">
              <h2 className="t-h3">Stockage et secrets</h2>
              <Facts
                rows={[
                  ['Enregistrements', p.storage.backend],
                  ['Bucket', p.storage.bucket ?? '—'],
                  ['Dépôt d’enregistrements', p.storage.recordingsEnabled ? 'Actif' : 'Inactif'],
                  ['Clé de chiffrement des secrets', p.secretsReady ? 'Présente' : 'Absente'],
                  ['Super-administrateurs désignés', String(p.superAdmins)],
                ]}
              />
            </section>
          </div>
          {p.links.length > 0 ? (
            <section className="zy-card bo-section">
              <h2 className="t-h3">Facturation réelle</h2>
              <p className="bo-meta">Le coût du serveur et de la base se lit chez le fournisseur.</p>
              <ul className="bo-links">
                {p.links.map((l) => (
                  <li key={l.url}>
                    <a href={l.url} target="_blank" rel="noreferrer noopener">
                      {l.label} ↗
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          <section className="zy-card bo-section">
            <div className="bo-section-head">
              <h2 className="t-h3">Dernières consultations du back-office</h2>
            </div>
            <div className="zy-table-cadre">
              <table className="zy-table zy-table--dense">
                <thead>
                  <tr>
                    <th>Quand</th>
                    <th>Qui</th>
                    <th>Onglet</th>
                  </tr>
                </thead>
                <tbody>
                  {p.accessLog.map((l, i) => (
                    <tr key={`${l.at}-${i}`}>
                      <td>{fmtDateTime(l.at)}</td>
                      <td>{l.email}</td>
                      <td>{l.section}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </Loaded>
  );
}

// --- Clés ----------------------------------------------------------------------

const errorOf = (cause: unknown) => (cause instanceof ApiRequestError ? cause.message : 'Une erreur inattendue est survenue.');

const SOURCE_LABELS = { backoffice: 'Enregistrée ici', serveur: 'Variable du serveur' } as const;

function EditableSetting({ setting }: { setting: BoSetting }) {
  const save = useSetBoKey();
  const clear = useClearBoKey();
  const check = useTestBoKey();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    check.reset();
    save.mutate(
      { key: setting.key, value },
      { onSuccess: () => { setEditing(false); setValue(''); } },
    );
  };
  const failure = save.error ?? clear.error ?? check.error;

  return (
    <div className="bo-key">
      <div className="bo-key-head">
        <div>
          <span className="bo-key-service">{setting.service}</span>
          <h3 className="bo-key-label">{setting.label}</h3>
        </div>
        <div className="bo-key-state">
          <span className={setting.present ? 'bo-pill is-on' : 'bo-pill'}>
            {setting.present ? (setting.display ?? 'Présente') : 'Absente'}
          </span>
          {setting.source ? <span className="bo-meta">{SOURCE_LABELS[setting.source]}</span> : null}
        </div>
      </div>
      {setting.updatedAt ? (
        <p className="bo-meta">
          Modifiée le {fmtDateTime(setting.updatedAt)}
          {setting.updatedBy ? ` par ${setting.updatedBy}` : ''}
        </p>
      ) : null}
      {setting.key === 'RO_LLM_PROVIDER' ? (
        <p className="bo-note">
          « vertex » garde les traitements d’IA dans l’Union européenne, comme l’annonce la politique de confidentialité.
          Passer à « anthropic » envoie les extraits hors UE : mettez la politique à jour avant.
        </p>
      ) : null}
      {editing ? (
        <form className="bo-key-form" onSubmit={submit}>
          {setting.choices ? (
            <label className="zy-field">
              <span className="zy-field__label">Nouvelle valeur</span>
              <Select value={value} onChange={(e) => setValue(e.target.value)} required>
                <option value="">Choisir…</option>
                {setting.choices.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </Select>
            </label>
          ) : (
            <Field
              label="Nouvelle clé"
              type="password"
              autoComplete="off"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              hint="Elle est chiffrée à l’enregistrement et ne sera plus jamais affichée."
              required
            />
          )}
          <div className="bo-key-actions">
            <Button type="submit" variant="accent" loading={save.isPending}>Enregistrer</Button>
            <Button type="button" variant="ghost" onClick={() => { setEditing(false); setValue(''); save.reset(); }}>Annuler</Button>
          </div>
        </form>
      ) : (
        <div className="bo-key-actions">
          <Button variant="ghost" onClick={() => setEditing(true)}>{setting.present ? 'Remplacer' : 'Ajouter'}</Button>
          {setting.testable && setting.present ? (
            <Button variant="ghost" loading={check.isPending} onClick={() => check.mutate(setting.key)}>Tester</Button>
          ) : null}
          {setting.source === 'backoffice' ? (
            <Button variant="ghost" loading={clear.isPending} onClick={() => { check.reset(); clear.mutate(setting.key); }}>
              Revenir à la valeur du serveur
            </Button>
          ) : null}
        </div>
      )}
      {check.data ? <p className={check.data.ok ? 'bo-result is-ok' : 'bo-result'}>{check.data.message}</p> : null}
      {failure ? <ErrorBanner>{errorOf(failure)}</ErrorBanner> : null}
    </div>
  );
}

// --- Mode maintenance ----------------------------------------------------------

const dt = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });

/** ISO → valeur d'un champ `datetime-local` (heure du navigateur). */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function MaintenanceTab() {
  const view = useBoMaintenance();
  return (
    <Loaded query={view}>
      {/* La clé recrée le formulaire quand la maintenance s'allume ou s'éteint. */}
      {(v) => <MaintenanceForm key={`${v.maintenance.active}-${v.maintenance.startedAt ?? ''}`} view={v} />}
    </Loaded>
  );
}

function MaintenanceForm({ view }: { view: BoMaintenanceView }) {
  const save = useBoSaveMaintenance();
  const me = useMe();
  const m = view.maintenance;
  const [message, setMessage] = useState(m.message);
  const [until, setUntil] = useState(m.until ? toLocalInput(m.until) : '');
  const [allowed, setAllowed] = useState<string[]>(m.allowedOrgIds);
  const [confirming, setConfirming] = useState(false);

  const self = me.data?.user.email.toLowerCase();
  // Les comptes qui verront la page de maintenance (vous gardez l'accès).
  const blocked = view.organizations
    .filter((o) => !allowed.includes(o.id))
    .flatMap((o) => o.emails)
    .filter((e) => e.toLowerCase() !== self).length;

  const toggle = (id: string) => setAllowed((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));
  const send = (active: boolean) => {
    setConfirming(false);
    save.mutate({ active, message, until: until ? new Date(until).toISOString() : null, allowedOrgIds: allowed });
  };

  return (
    <section className="zy-card bo-section bo-maintenance">
      <div className="bo-section-head">
        <h2 className="t-h3">Mode maintenance</h2>
        <span className="bo-meta">
          {m.active && m.startedAt
            ? `Actif depuis le ${dt.format(new Date(m.startedAt))}${m.startedBy ? ` (${m.startedBy})` : ''}`
            : 'Éteint : tout le monde accède à l’application.'}
        </span>
      </div>
      <p className="bo-note">
        Pendant la maintenance, les comptes non cochés ci-dessous voient une page « Maintenance en cours » avec votre
        message ; vous gardez l’accès, comme tout super-administrateur. Les liens publics (rendez-vous, questionnaire,
        invitations) restent ouverts ; l’inscription et la mesure automatique attendent. Prise en compte en 10 secondes.
      </p>

      <label className="zy-field">
        <span className="zy-field__label">Message affiché</span>
        <textarea className="zy-input" rows={3} maxLength={500} value={message} onChange={(e) => setMessage(e.target.value)} />
      </label>
      <label className="zy-field">
        <span className="zy-field__label">Fin prévue (facultatif, annoncée aux utilisateurs)</span>
        <input className="zy-input" type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} />
      </label>

      <fieldset className="bo-maintenance-orgs">
        <legend className="zy-field__label">Organisations qui gardent l’accès</legend>
        {view.organizations.map((o) => (
          <label key={o.id} className="bo-maintenance-org">
            <input className="zy-check" type="checkbox" checked={allowed.includes(o.id)} onChange={() => toggle(o.id)} />
            <span>
              <strong>{o.name}</strong>
              <span className="bo-meta"> · {o.emails.join(', ') || 'aucun compte'}</span>
            </span>
          </label>
        ))}
      </fieldset>

      {save.isError ? (
        <ErrorBanner>{save.error instanceof ApiRequestError ? save.error.message : 'Enregistrement impossible.'}</ErrorBanner>
      ) : null}

      <div className="bo-actions">
        {m.active ? (
          <>
            <Button variant="ghost" onClick={() => send(true)} loading={save.isPending}>
              Enregistrer les changements
            </Button>
            <Button variant="accent" onClick={() => send(false)} loading={save.isPending}>
              Désactiver la maintenance
            </Button>
          </>
        ) : confirming ? (
          <>
            <span className="bo-meta">
              {blocked} compte{blocked > 1 ? 's' : ''} verr{blocked > 1 ? 'ont' : 'a'} la page de maintenance. Confirmer ?
            </span>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Annuler
            </Button>
            <Button variant="accent" onClick={() => send(true)} loading={save.isPending}>
              Confirmer
            </Button>
          </>
        ) : (
          <Button variant="accent" onClick={() => setConfirming(true)}>
            Activer la maintenance
          </Button>
        )}
      </div>
    </section>
  );
}

function KeysTab() {
  const keys = useBoKeys();
  return (
    <Loaded query={keys}>
      {(k) => (
        <>
          <section className="zy-card bo-section">
            <div className="bo-section-head">
              <h2 className="t-h3">Modifiables ici</h2>
              <span className="bo-meta">Prise en compte en moins d’une minute, sans redéploiement.</span>
            </div>
            {k.settings.filter((x) => x.editable).map((x) => (
              <EditableSetting key={x.key} setting={x} />
            ))}
          </section>
          <section className="zy-card bo-section">
            <div className="bo-section-head">
              <h2 className="t-h3">Gérées par le serveur</h2>
              <span className="bo-meta">Visibles pour savoir qu’elles existent ; elles se changent dans Google Cloud.</span>
            </div>
            <div className="zy-table-cadre">
              <table className="zy-table zy-table--dense bo-table-wrap">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Réglage</th>
                    <th>État</th>
                    <th>Remarque</th>
                  </tr>
                </thead>
                <tbody>
                  {k.settings.filter((x) => !x.editable).map((x) => (
                    <tr key={x.key}>
                      <td>{x.service}</td>
                      <td>{x.label}</td>
                      <td>{x.present ? (x.display ?? 'Présente') : 'Absente'}</td>
                      <td className="wrap">{x.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <p className="bo-note">
            Les clés des connecteurs (Matomo, Jira, GA4, PostHog, Lookback) appartiennent à chaque projet client : elles
            se gèrent dans ses Options, pas ici.
          </p>
        </>
      )}
    </Loaded>
  );
}

export function BackofficePage({ tab }: { tab: string }) {
  const current: Tab = TABS.some(([key]) => key === tab) ? (tab as Tab) : 'comptes';
  return (
    <div className="app-main backoffice">
      <div className="zy-page-head__texte">
        <Eyebrow>Exploitant</Eyebrow>
        <h1 className="t-h1">Back-office</h1>
        <p className="zy-intro">Qui utilise Zygosight, quel volume, à quel coût d’IA, et sur quoi le serveur est branché.</p>
      </div>
      <nav className="bo-tabs" aria-label="Sections du back-office">
        {TABS.map(([key, label]) => (
          <Link key={key} to={`/backoffice/${key}`} className={key === current ? 'bo-tab is-active' : 'bo-tab'}>
            {label}
          </Link>
        ))}
      </nav>
      {current === 'comptes' ? <AccountsTab /> : null}
      {current === 'donnees' ? <DataTab /> : null}
      {current === 'ia' ? <AiTab /> : null}
      {current === 'plateforme' ? <PlatformTab /> : null}
      {current === 'cles' ? <KeysTab /> : null}
      {current === 'maintenance' ? <MaintenanceTab /> : null}
      {current === 'retours' ? <FeedbackTab /> : null}
    </div>
  );
}
